/**
 * React Query hooks for the billing module.
 *
 * Read-only — Plan 12-03 has no mutations (subscription cancel/refund flows
 * are explicitly out of scope per 12-CONTEXT.md `<deferred>`).
 *
 * staleTime is 60 seconds because Stripe data is eventual-consistency
 * (webhooks arrive async, rate limits matter, MRR doesn't change every
 * second). The same staleTime is used for all three hooks for consistency.
 */

import { useQuery } from '@tanstack/react-query'

import {
  fetchSubscriptionsSummary,
  fetchSubscriptionsEvents,
  fetchUserPaymentHistory,
} from './billingApi'
import { billingKeys } from './billingKeys'

const SIXTY_SECONDS = 60 * 1000

/**
 * Fetch the subscriptions summary tile data (MRR + active + failed counts).
 */
export function useSubscriptionsSummary() {
  return useQuery({
    queryKey: billingKeys.summary(),
    queryFn: fetchSubscriptionsSummary,
    staleTime: SIXTY_SECONDS,
  })
}

/**
 * Fetch the recent N billing events for the events feed.
 *
 * `limit` is capped at 20 client-side inside `fetchSubscriptionsEvents`,
 * but is included in the queryKey so callers can request smaller pages
 * (e.g. on a dashboard) without colliding with the full 20-row feed.
 */
export function useSubscriptionsEvents(limit = 20) {
  return useQuery({
    queryKey: billingKeys.events(limit),
    queryFn: () => fetchSubscriptionsEvents(limit),
    staleTime: SIXTY_SECONDS,
  })
}

/**
 * Fetch payment history for a single user, by user UUID.
 *
 * `enabled: !!userId` prevents the hook from firing for unauthenticated
 * routes or when the parent route's :id param hasn't materialised yet.
 */
export function useUserPaymentHistory(userId: string) {
  return useQuery({
    queryKey: billingKeys.userPayments(userId),
    queryFn: () => fetchUserPaymentHistory(userId),
    staleTime: SIXTY_SECONDS,
    enabled: !!userId,
  })
}
